import {
  CompoundButton,
  Dropdown,
  getTheme,
  IDropdownOption,
  PrimaryButton,
  Stack,
  Text,
  TextField
} from "@fluentui/react";
import { useFormik } from "formik";
import { Fragment, useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import * as Yup from "yup";

import { ProductContractList } from "../ProductOffers/ProductOfferContract/ProductContractList";
import { FiltersOptions } from "../ProductOffers/ProductOfferFilters/ProductFilter.styles";
import { CONTRACT_TYPES } from "../utils/constants";

import { EnergyOfferDescription } from "./EnergyOfferDescription";

import { Contract } from "@/components/Contracts/contract.interface";
import { AvailableOffer } from "@/components/Contracts/ItemsStep/EnergyItem";
import { AdditionalOfferPriceToAppend } from "@/components/Offers/AdditionalComponents/AdditionalOfferPriceToAppend";
import { IAdditionalOfferPrice } from "@/components/Settings/AdditionalComponents/types";
import { Card } from "@/components/Shared/Card";
import { useGet } from "@/core/libs/api/react-query/hook/use-get";
import { useApi } from "@/core/libs/api/react-query/useApi";
import { useSelectedBu } from "@/hooks/useSelectedBu";
import { Plan, PlanOfferItem } from "@/modules/Offers/types/PlansOffers.types";
import { ProductContractType } from "@/modules/Offers/types/ProductsOffers.types";
import { ENERGY_PROVIDER_OPTIONS } from "@/modules/Settings/constants/constants";
import { formatToSelect } from "@/utils/FormatToSelect/format-to-select";

interface EnergyOfferContractProps {
  selectedPlan: Plan;
  selectedCarrier: PlanOfferItem;
}

interface FormValues {
  operationId: number | null;
  energyInstallationNumber: string;
  energyProvider: string;
  contractId: number | null;
}

const initialValues: FormValues = {
  operationId: null,
  energyInstallationNumber: "",
  energyProvider: "",
  contractId: null
};

const theme = getTheme();

function getValidationSchema(contractType: ProductContractType) {
  return Yup.object({
    operationId: Yup.number().nullable().required("Selecione uma operação"),
    energyInstallationNumber: Yup.string()
      .required("Instalação de ativação de energia é obrigatória")
      .trim(),
    energyProvider: Yup.string()
      .required("Selecione uma concessionária local")
      .trim(),
    contractId:
      contractType === ProductContractType.EXISTENT
        ? Yup.number().nullable().required("Selecione um contrato")
        : Yup.number().nullable()
  });
}

interface FormCardProps {
  title: string;
  children: React.ReactNode;
}

function FormCard({ title, children }: FormCardProps): JSX.Element {
  return (
    <Card
      style={{
        padding: 30,
        width: "100%",
        display: "flex",
        flexDirection: "column",
        alignItems: "center"
      }}
    >
      <Text
        variant="xLarge"
        block
        style={{
          fontWeight: 600,
          textAlign: "center",
          paddingBottom: 20
        }}
      >
        {title}
      </Text>
      {children}
    </Card>
  );
}

export function EnergyOfferContract({
  selectedPlan,
  selectedCarrier
}: EnergyOfferContractProps): JSX.Element {
  const { selectedBU } = useSelectedBu();
  const { execute } = useApi();
  const navigate = useNavigate();

  const { data: availableOffers } = useGet<Array<AvailableOffer>>({
    queryToken: `energy-offers-${selectedPlan.service.id}-${selectedBU?.id}`,
    url: `contracts/offers`,
    params: {
      service: selectedPlan.service.id,
      businessActionId: selectedBU?.id
    },
    enabled: Boolean(selectedBU?.id && selectedPlan?.service?.id)
  });

  const [contractType, setContractType] = useState<ProductContractType>(null);

  // Até que o componente ProductContractList seja revisado
  // selectedContract é  tanto o id do contrato quanto o próprio objeto
  const [selectedContract, setSelectedContract] = useState<number | Contract>();
  const [isAdditionalModalOpen, setIsAdditionalModalOpen] = useState(false);
  const [isCreating, setIsCreating] = useState(false);

  const handleFinalSubmit = useCallback(
    async (
      additionalOfferPrice: Array<IAdditionalOfferPrice>,
      formValues: FormValues
    ): Promise<void> => {
      if (!selectedBU || !selectedCarrier) return;

      setIsCreating(true);

      const payload = {
        operation: formValues.operationId,
        offerPrice: selectedPlan.offer.offerPrice.id,
        plan: selectedPlan.id,
        additionalInfo: {
          planIsConectivityOrSmart: false,
          energyInstallationNumber: formValues.energyInstallationNumber,
          energyProvider: formValues.energyProvider
        },
        additionalContractItems: additionalOfferPrice
      };

      if (
        contractType === ProductContractType.EXISTENT &&
        formValues.contractId
      ) {
        const response = await execute({
          url: `contracts/${formValues.contractId}/item?businessActionId=${selectedBU.id}`,
          method: "POST",
          bodyObj: payload,
          showAlert: true,
          messages: {
            success: "Item adicionado com sucesso!",
            error: "Erro ao adicionar item ao contrato."
          },
          setIsFetching: setIsCreating
        });

        if (response?.response?.ok === true) {
          setIsAdditionalModalOpen(false);

          navigate(`/contracts/${formValues.contractId}/edit`);
        }
      } else {
        const response = await execute<{ contract: number }>({
          url: `contracts/item?businessActionId=${selectedBU.id}`,
          method: "POST",
          bodyObj: payload,
          showAlert: true,
          messages: {
            success: "Contrato criado com sucesso!",
            error: "Erro ao criar contrato."
          },
          setIsFetching: setIsCreating
        });

        if (response?.response?.ok === true) {
          setIsAdditionalModalOpen(false);

          const contractId = response?.data?.contract;

          if (contractId) {
            navigate(`/contracts/${contractId}/edit`);
          }
        }
      }
    },
    [contractType, selectedBU, selectedPlan, selectedCarrier, execute, navigate]
  );

  const formik = useFormik<FormValues>({
    initialValues,
    validationSchema: getValidationSchema(contractType),
    enableReinitialize: false,
    onSubmit: (values, { setSubmitting }) => {
      setSubmitting(false);

      if (selectedPlan.offer.offerPrice.additionalOfferPrice?.length > 0) {
        setIsAdditionalModalOpen(true);
      } else {
        handleFinalSubmit([], values).catch(console.error);
      }
    }
  });

  const {
    values,
    errors,
    touched,
    setFieldValue,
    setFieldTouched,
    handleSubmit
  } = formik;

  // Esse useEffect é necessário porque o selectedContract pode ser atualizado pelo componente ProductContractList,
  // e precisamos garantir que o formik seja atualizado com o contractId correto sempre que o selectedContract ou
  // contractType mudarem. Remover quando revisarmos o componente ProductContractList para que ele retorne sempre
  //  apenas o id do contrato selecionado, e não o objeto inteiro.

  useEffect(() => {
    if (contractType === ProductContractType.EXISTENT && selectedContract) {
      setFieldValue(
        "contractId",
        typeof selectedContract === "object"
          ? selectedContract?.id
          : selectedContract
      ).catch(console.error);
    } else {
      setFieldValue("contractId", null).catch(console.error);
    }
  }, [contractType, selectedContract, setFieldValue]);

  const getFieldError = (fieldName: keyof FormValues): string | undefined => {
    return touched[fieldName] && errors[fieldName]
      ? errors[fieldName]
      : undefined;
  };

  const handleFormSubmit = (e: React.FormEvent<HTMLFormElement>): void => {
    e.preventDefault();
    handleSubmit();
  };

  const handleOperationChange = (
    _: React.FormEvent<HTMLDivElement>,
    option?: IDropdownOption
  ): void => {
    setFieldValue("operationId", option?.key ?? null).catch(console.error);
  };

  const handleEnergyInstallationChange = (
    _: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>,
    newValue?: string
  ): void => {
    setFieldValue("energyInstallationNumber", newValue ?? "").catch(
      console.error
    );
  };

  const handleEnergyProviderChange = (
    _: React.FormEvent<HTMLDivElement>,
    option?: IDropdownOption
  ): void => {
    setFieldValue("energyProvider", option?.key ?? "").catch(console.error);
  };

  const handleOperationBlur = (): void => {
    setFieldTouched("operationId", true).catch(console.error);
  };

  const handleEnergyInstallationBlur = (): void => {
    setFieldTouched("energyInstallationNumber", true).catch(console.error);
  };

  const handleEnergyProviderBlur = (): void => {
    setFieldTouched("energyProvider", true).catch(console.error);
  };

  const handleAdditionalSubmit = (
    additionalOfferPrice: Array<IAdditionalOfferPrice>
  ): void => {
    handleFinalSubmit(additionalOfferPrice, formik.values).catch(console.error);
  };

  const handleContractTypeChange = (type: ProductContractType): void => {
    setContractType(type);
  };

  return (
    <Stack horizontal styles={{ root: { minHeight: 800 } }}>
      <EnergyOfferDescription
        selectedPlan={selectedPlan}
        selectedCarrier={selectedCarrier}
      />
      <Stack
        verticalAlign="start"
        tokens={{ childrenGap: 20 }}
        styles={{
          root: {
            width: "100%",
            maxWidth: 800,
            padding: 40,
            backgroundColor: theme.palette.neutralLighterAlt,
            overflowY: "auto",
            "& *": { boxSizing: "border-box" }
          }
        }}
      >
        <Card style={{ padding: 30, width: "100%", height: 180 }}>
          <Text variant="xLarge" block style={{ marginBottom: 20 }}>
            Deseja criar um novo contrato ou selecionar um já existente?
          </Text>

          <FiltersOptions>
            {CONTRACT_TYPES.map(({ key, text }) => (
              <CompoundButton
                key={key}
                id={`contractTypeOptions-button-${key}`}
                checked={contractType === key}
                onClick={() => handleContractTypeChange(key)}
                style={{
                  width: "100px",
                  border: "0",
                  borderRadius: "10px"
                }}
              >
                <Text variant="mediumPlus" style={{ fontWeight: 600 }}>
                  {text}
                </Text>
              </CompoundButton>
            ))}
          </FiltersOptions>
        </Card>

        {contractType === ProductContractType.EXISTENT && (
          <Card style={{ padding: 30, width: "100%" }}>
            <Text
              variant="xLarge"
              block
              style={{ fontWeight: 600, marginBottom: 16, textAlign: "center" }}
            >
              Selecione um contrato existente para prosseguir
            </Text>
            <ProductContractList setSelectedContract={setSelectedContract} />
            {getFieldError("contractId") && (
              <span
                style={{
                  color: "#a4262c",
                  fontSize: 12,
                  marginTop: 8,
                  display: "block"
                }}
              >
                {getFieldError("contractId")}
              </span>
            )}
          </Card>
        )}

        <form
          onSubmit={handleFormSubmit}
          style={{ width: "100%", flex: 1, display: "flex" }}
        >
          <Stack
            tokens={{ childrenGap: 20 }}
            styles={{ root: { flex: 1, justifyContent: "space-between" } }}
          >
            {contractType && (
              <Fragment>
                <FormCard title="Selecione o tipo de operação">
                  <Dropdown
                    id="select-contract-operation"
                    placeholder="Selecione uma opção"
                    required
                    selectedKey={values.operationId}
                    options={formatToSelect(availableOffers || [])}
                    onChange={handleOperationChange}
                    onBlur={handleOperationBlur}
                    errorMessage={getFieldError("operationId")}
                    styles={{ root: { maxWidth: 400, width: "100%" } }}
                  />
                </FormCard>

                <FormCard title="Concessionária local">
                  <Dropdown
                    id="energy-provider"
                    placeholder="Selecione a concessionária local"
                    required
                    selectedKey={values.energyProvider}
                    options={ENERGY_PROVIDER_OPTIONS}
                    onChange={handleEnergyProviderChange}
                    onBlur={handleEnergyProviderBlur}
                    errorMessage={getFieldError("energyProvider")}
                    styles={{ root: { maxWidth: 400, width: "100%" } }}
                  />
                </FormCard>

                <FormCard title="Unidade Consumidora">
                  <TextField
                    id="energy-installation-number"
                    placeholder="Unidade Consumidora"
                    required
                    value={values.energyInstallationNumber}
                    onChange={handleEnergyInstallationChange}
                    onBlur={handleEnergyInstallationBlur}
                    errorMessage={getFieldError("energyInstallationNumber")}
                    styles={{ root: { maxWidth: 400, width: "100%" } }}
                  />
                </FormCard>
              </Fragment>
            )}

            <Stack
              horizontal
              horizontalAlign="center"
              tokens={{ childrenGap: 8 }}
              styles={{
                root: { paddingTop: 10, width: "100%", marginTop: "auto" }
              }}
            >
              <PrimaryButton
                id="submit-button"
                type="submit"
                style={{
                  padding: "24px 28px",
                  minWidth: "200px"
                }}
                disabled={isCreating || !contractType}
              >
                Adicionar Benefício Combo
              </PrimaryButton>
            </Stack>
          </Stack>
        </form>
      </Stack>

      {selectedPlan.offer.offerPrice.additionalOfferPrice && (
        <AdditionalOfferPriceToAppend
          additionalOfferPriceList={
            selectedPlan.offer.offerPrice.additionalOfferPrice
          }
          isModalOpen={isAdditionalModalOpen}
          setIsModalOpen={setIsAdditionalModalOpen}
          submit={handleAdditionalSubmit}
          isSubmitting={isCreating}
          submitMessage={""}
          clearSubmitMessage={() => {}}
          canEdit={false}
          planPrice={parseFloat(
            selectedPlan.offer.offerPrice.price?.replace(",", ".") || "0"
          )}
        />
      )}
    </Stack>
  );
}
